import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "search-relevance-tuning",
  kind: "horizontal",
  title: "Custom Search & Discovery Systems",
  shortTitle: "Custom Search",
  status: "draft",
  tier: "we-do",
  seoTitle: "Custom Search & Discovery Systems | General Applications",
  seoDescription:
    "Custom keyword, semantic, faceted, and hybrid search systems for content libraries, operational records, and media collections.",
  summary:
    "Custom search systems that bring keyword, semantic, metadata, filters, sorting, and relevance control together so people can find the right item in a collection that has outgrown browsing.",
  image: "/images/blog/search-relevance-taxonomy-hero.webp",
  relatedCapabilitySlugs: ["intelligent-systems", "data-search-knowledge"],
  relatedIndustrySlugs: [
    "enterprise",
    "healthcare",
    "military",
    "media-entertainment",
  ],
  relatedPostSlugs: [
    "search-relevance-taxonomy",
    "reconciling-fragmented-data",
    "reconciling-fragmented-enterprise-identifiers",
  ],
  engineering: [
    "Keyword, phrase, identifier, semantic, and hybrid retrieval architecture",
    "Facets, filters, sorting, pagination, and query-state design for large collections",
    "Metadata, taxonomy, canonical-identifier, and indexing models",
    "Relevance evaluation, result-quality diagnosis, and iterative ranking controls",
    "Search interfaces for documents, operational records, product data, and media assets",
    "Large-file and small-record discovery patterns across shared content collections",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Search Is More Than a Box",
    },
    {
      type: "paragraph",
      text: "A useful search system has to recognize what is being sought, what may be filtered out, how a result should be ordered, and enough context for the person looking to decide whether they have found the right thing. The right approach may be exact matching, full text, semantic retrieval, faceted navigation, a curated browse path, or a combination of all of them.",
    },
    {
      type: "heading",
      level: 2,
      text: "Large Collections Need Structure Before Ranking",
    },
    {
      type: "paragraph",
      text: "Media collections make the problem visible: a thumbnail, proxy, master, subtitle file, and project record may all describe the same work, while a tiny metadata record may matter as much as a large asset. Good discovery depends on a model that connects those objects, carries their rights and lifecycle state, and lets filtering happen before ranking obscures the relevant set.",
    },
  ],
  phases: {
    study: {
      focus:
        "Identify what people need to find, the collections and metadata that describe it, the queries that fail today, and the decisions a result must support.",
      produces: [
        "Search audience, task, query, and decision inventory",
        "Content, asset, record, and metadata source map",
        "Current retrieval, browse, filter, and relevance failure analysis",
        "Authority, access, lifecycle, and performance constraint definition",
      ],
      fit: {
        good: [
          "Organizations with content, records, products, or media collections too large or varied to navigate reliably by folders and ad hoc browsing",
          "Teams that need exact, semantic, faceted, and filtered retrieval to work together without hiding the source and metadata behind a single ranking score",
        ],
        poor: [
          "A request to add vector search before identifying the content model, retrieval tasks, filters, and result-quality criteria",
          "A collection with no accessible source records, ownership, or metadata from which a useful index can be built",
        ],
      },
    },
    "initial-design": {
      focus:
        "Define the discovery model and choose the retrieval methods, filters, sort orders, and result context that suit each search task.",
      produces: [
        "Search, browse, taxonomy, and query-state architecture",
        "Keyword, semantic, faceted, and hybrid retrieval selection",
        "Metadata, identifier, relationship, and indexing model",
        "Relevance measurement and result-quality evaluation plan",
      ],
    },
    "detailed-design": {
      focus:
        "Specify ingest, indexing, query, ranking, interface, and evaluation behavior so the search system stays understandable as collections change.",
      produces: [
        "Ingest, transformation, indexing, and update specifications",
        "Filter, sort, pagination, permission, and empty-result behavior",
        "Result presentation and source-context requirements",
        "Query set, relevance judgments, and regression-evaluation design",
      ],
    },
    implementation: {
      focus:
        "Build the discovery system against representative collections and validate it with the people who need to find and act on the results.",
      produces: [
        "Search, browse, filter, and sorting interfaces",
        "Indexed and connected content, record, and asset collections",
        "Relevance evaluation and operational monitoring controls",
        "Guidance for metadata stewardship and ongoing search improvement",
      ],
    },
  },
};
